// Thin wrappers over /api/tasks. Every write returns the authoritative rows it
// touched, which TasksContext merges over its optimistic copy.
import api from './api';
import {
  RecurrenceRule,
  TaskHoliday,
  ScheduleTarget,
  TaskCompletion,
  TaskItem,
  TaskList,
  TaskNotification,
  TaskSchedule,
  TaskUser,
  TemplateLine,
} from '../types/tasks';

export interface MemberRow {
  list_id: number;
  email: string;
}

export interface BootstrapPayload {
  today: string;
  weekly_list_id: number;
  /** Sees the whole MPP week list; false = only lines assigned to them on it. */
  weekly_viewer: boolean;
  rev: number;
  users: TaskUser[];
  lists: TaskList[];
  items: TaskItem[];
  members: MemberRow[];
  notifications: TaskNotification[];
  pinned_list_ids: number[];
}

export interface SyncPayload {
  today: string;
  rev: number;
  lists: TaskList[];
  items: TaskItem[];
  members: MemberRow[];
  member_list_ids: number[];
  visible_list_ids: number[];
  /** Changed lines on a week list that aren't shown to me (any more). */
  hidden_item_ids: number[];
  weekly_viewer: boolean;
  notifications: TaskNotification[];
  pinned_list_ids: number[];
}

export interface ItemsResult {
  item?: TaskItem;
  items: TaskItem[];
  members?: MemberRow[];
}

export interface NotificationRule {
  category: 'restock_dates' | 'inputs_low' | 'holidays' | 'warehouse_requests';
  enabled: boolean;
  assignee_email: string | null;
  lead_days: number;
}

export interface NotificationSettings {
  rules: NotificationRule[];
  prefs: { email: string; digest_enabled: boolean; weekly_list: boolean }[];
}

export interface ForeignList {
  id: number;
  title: string;
  owner_email: string;
  kind: string;
  color: string | null;
}

/** A warehouse Custom Request: its own list, plus a linked copy on the MPP week list. */
export interface RequestInput {
  title: string;
  lines: string[];
  warehouse_id?: number | null;
  input_id?: number | null;
}

export interface ScheduleInput {
  title: string;
  target: ScheduleTarget;
  target_list_id?: number | null;
  start_date: string;
  recurrence: RecurrenceRule | null;
  items: TemplateLine[];
  note: string | null;
  assignees: string[];
  active?: boolean;
}

const get = async <T>(r: string, params: Record<string, unknown> = {}): Promise<T> =>
  (await api.get('/tasks', { params: { r, ...params } })).data;

const post = async <T>(r: string, body: unknown): Promise<T> =>
  (await api.post('/tasks', body, { params: { r } })).data;

export const taskApi = {
  bootstrap: () => get<BootstrapPayload>('bootstrap'),
  sync: (since: number) => get<SyncPayload>('sync', { since }),
  archived: () => get<{ lists: TaskList[]; items: TaskItem[]; members: MemberRow[] }>('archived'),
  /** Other people's lists, to hand work straight onto them (admins only; others get []). */
  listsFor: (emails: string[]) => get<{ lists: ForeignList[] }>('lists-for', { emails: emails.join(',') }),
  calendar: (from: string, to: string) =>
    get<{ completions: TaskCompletion[]; schedules: TaskSchedule[]; holidays: TaskHoliday[] }>('calendar', { from, to }),

  createList: (body: { title: string; color?: string | null; members?: string[]; items?: TemplateLine[] }) =>
    post<{ list: TaskList; items: TaskItem[]; members: MemberRow[] }>('list-create', body),
  updateList: (body: { id: number; title?: string; color?: string | null; archived?: boolean; project_start?: string | null }) =>
    post<{ list: TaskList; items?: TaskItem[] }>('list-update', body),
  setMembers: (id: number, emails: string[]) =>
    post<{ list_id: number; members: MemberRow[] }>('list-members', { id, emails }),
  deleteList: (id: number) => post<{ id: number }>('list-delete', { id }),
  createProject: (body: { title: string; color?: string | null }) =>
    post<{ list: TaskList; items: TaskItem[]; members: MemberRow[] }>('project-create', body),
  /** "Show on Lists": a Lists card for a project branch (no root = the whole project). */
  createView: (projectId: number, rootItemId: number | null, filter: 'branch' | 'materials' = 'branch') =>
    post<{ list: TaskList; members: MemberRow[] }>('view-create', { project_id: projectId, root_item_id: rootItemId, filter }),

  createItem: (body: Partial<TaskItem> & { list_id: number; text: string }) => post<ItemsResult>('item-create', body),
  updateItem: (id: number, patch: Partial<TaskItem>) => post<ItemsResult>('item-update', { id, ...patch }),
  deleteItem: (id: number) => post<ItemsResult>('item-delete', { id }),
  moveItems: (listId: number, moves: { id: number; parent_id: number | null; depth: number; sort_order: number }[]) =>
    post<ItemsResult>('items-move', { list_id: listId, moves }),

  uploadImage: (body: { list_id: number; filename: string; contentType: string; data: string }) =>
    post<{ list: TaskList }>('image-upload', body),
  deleteImage: (listId: number, url: string) => post<{ list: TaskList }>('image-delete', { list_id: listId, url }),

  linkToWeek: (id: number) => post<ItemsResult>('item-link-week', { id }),
  unlinkFromWeek: (id: number) => post<ItemsResult>('item-unlink-week', { id }),
  pinList: (id: number, pinned: boolean) => post<{ pinned_list_ids: number[] }>('list-pin', { id, pinned }),
  createRequest: (body: RequestInput) =>
    post<{ list: TaskList; items: TaskItem[]; members: MemberRow[] }>('request-create', body),

  holidays: () => get<{ holidays: TaskHoliday[] }>('holidays'),
  createHoliday: (day: string, name: string) => post<{ holidays: TaskHoliday[] }>('holiday-create', { day, name }),
  deleteHoliday: (id: number) => post<{ holidays: TaskHoliday[] }>('holiday-delete', { id }),
  addDefaultHolidays: (year: number) => post<{ holidays: TaskHoliday[] }>('holiday-defaults', { year }),

  createSchedule: (body: ScheduleInput) => post<{ schedule: TaskSchedule; added: boolean }>('schedule-create', body),
  updateSchedule: (id: number, body: Partial<ScheduleInput>) =>
    post<{ schedule: TaskSchedule }>('schedule-update', { id, ...body }),
  deleteSchedule: (id: number) => post<{ id: number }>('schedule-delete', { id }),

  markSeen: (body: { ids?: number[]; all?: boolean }) =>
    post<{ notifications: TaskNotification[] }>('notifications-seen', body),

  notificationSettings: () => get<NotificationSettings>('notification-settings'),
  saveNotificationSettings: (body: NotificationSettings) =>
    post<NotificationSettings>('notification-settings', body),
};

/** The server's error message, which is written for people. */
export function taskErrorMessage(error: any, fallback = 'Something went wrong'): string {
  return error?.response?.data?.error || error?.message || fallback;
}
